import { Injectable } from '@angular/core';
import { DataStorage__factory } from '@data/abi';
import { TransactionDataModel } from '@models/transaction-data.model';
import { ErrorService } from '@services/error.service';
import { FeesExtension, ON_CHAIN_CALL_DELAY, ON_CHAIN_CALL_RETRY } from '@services/onchain/FeesExtension';
import { ProviderService } from '@services/provider.service';
import { GET_CORE_ADDRESSES } from '@shared/constants/addresses/addresses.constant';
import { adjustGasLimit } from '@shared/utils';
import { NGXLogger } from 'ngx-logger';
import { catchError, concatMap, from, retry, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DataStorageService extends FeesExtension {
  constructor(
    private providerService: ProviderService,
    private logger: NGXLogger,
    private errorService: ErrorService,
  ) {
    super();
  }

  // --- FACTORIES ---

  private createDataStorage(chainId: number) {
    return DataStorage__factory.connect(
      GET_CORE_ADDRESSES(chainId).dataStorage,
      this.providerService.getProviderForRead(),
    );
  }

  // --- VIEWS ---

  getData$(chainId: number, account: string) {
    this.logger.trace('retrieve', chainId);
    return from(this.createDataStorage(chainId).data(account)).pipe(
      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      catchError(this.errorService.onCatchError),
    );
  }

  // --- CALLS ---

  store$(chainId: number, data: string) {
    this.logger.trace('store', chainId);
    return from(this.createDataStorage(chainId).store.estimateGas(data)).pipe(
      switchMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),

      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      concatMap(gas => {
        return this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'Store',
            txPopulated: this.createDataStorage(chainId).store.populateTransaction(data),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: false,
          }),
        );
      }),
    );
  }

  clearData$(chainId: number) {
    this.logger.trace('clearData', chainId);
    return from(this.createDataStorage(chainId).clearData.estimateGas()).pipe(
      switchMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),

      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      concatMap(gas => {
        return this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'ClearData',
            txPopulated: this.createDataStorage(chainId).clearData.populateTransaction(),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: false,
          }),
        );
      }),
    );
  }
}
